import { useEffect, useState, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Plus, Loader2, FileDown, Trash2, Pencil, AlertTriangle, Upload, X, Wallet, CheckCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fmtXof, fmtDate, apiError } from "../../lib/api";
import { TrendAnalytics } from "./TrendAnalytics";
import { RankingCard } from "./RankingCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const NATURES = ["Vente directe", "Prestation de service", "Devis accepté et payé", "Subvention / Don", "Remboursement reçu", "Autre"];
const MODES = ["Orange Money", "MTN Mobile Money", "Wave", "Virement bancaire", "Espèces", "Carte bancaire", "CinetPay"];
const CATEGORIES = [
  "Hébergement et infrastructure (Emergent, domaines)", "Abonnements logiciels et outils",
  "Frais de communication (téléphone, internet)", "Marketing et publicité", "Formation et documentation",
  "Honoraires et sous-traitance", "Matériel informatique", "Frais bancaires et de transaction",
  "Taxes et impôts", "Salaires et rémunérations", "Frais de déplacement", "Charges diverses", "Autre",
];
const TVA_OPTIONS = [0, 18, 20];
const PIE_COLORS = ["#0284C7", "#16a34a", "#f59e0b", "#7c3aed", "#dc2626", "#0891b2", "#db2777", "#65a30d", "#ea580c", "#4f46e5", "#0d9488", "#94a3b8"];

const conv = (xof, fx) => {
  if (!fx) return "";
  return `≈ ${(xof / fx.EUR).toFixed(0)} € · ${(xof / fx.USD).toFixed(0)} $`;
};

function KPI({ label, value, sub, accent, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{label}</p>
      <p className={`mt-2 font-display text-xl font-bold ${accent || "text-navy"}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function Justificatif({ path, name, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/finance/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path, data.filename);
      toast.success("Pièce jointe téléversée");
    } catch (er) { toast.error(apiError(er)); } finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="text-xs font-medium text-slate-500">Pièce justificative (PDF/image)</Label>
      {path ? (
        <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-700">
          <span className="flex-1 truncate">{name || "Fichier joint"}</span>
          <button type="button" onClick={() => onChange("", "")} className="text-red-500" data-testid={`${testid}-remove`}><X size={14} /></button>
        </div>
      ) : (
        <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 p-2.5 text-xs text-slate-500 hover:border-ciel" data-testid={testid}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? "Téléversement…" : "Ajouter un fichier"}
          <input type="file" className="hidden" accept="application/pdf,image/*" onChange={upload} disabled={busy} />
        </label>
      )}
    </div>
  );
}

export function FinanceManager() {
  const [dash, setDash] = useState(null);
  const [recettes, setRecettes] = useState({ auto: [], manuel: [], fx: null });
  const [depenses, setDepenses] = useState({ depenses: [], fx: null });
  const [recDialog, setRecDialog] = useState(false);
  const [depDialog, setDepDialog] = useState(false);
  const [recForm, setRecForm] = useState(null);
  const [depForm, setDepForm] = useState(null);
  const [tres, setTres] = useState({ solde_initial: 0, seuil_alerte: 0, objectif_ca_mensuel: 0, objectif_ca_annuel: 0 });
  const [reportY, setReportY] = useState(new Date().getFullYear());
  const [reportM, setReportM] = useState("");
  const [reportQ, setReportQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, r, dp] = await Promise.all([
        api.get("/admin/finance/dashboard"), api.get("/admin/finance/recettes"), api.get("/admin/finance/depenses"),
      ]);
      setDash(d.data); setRecettes(r.data); setDepenses(dp.data);
      setTres({ solde_initial: d.data.tresorerie.solde_initial, seuil_alerte: d.data.tresorerie.seuil_alerte, objectif_ca_mensuel: d.data.objectif_ca_mensuel || 0, objectif_ca_annuel: d.data.objectif_ca_annuel || 0 });
    } catch (e) { toast.error(apiError(e, "Erreur de chargement financier")); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fx = dash?.fx;

  const openRec = (r) => setRecForm(r || { date: new Date().toISOString().slice(0, 10), nature: NATURES[0], nature_autre: "", description: "", amount_ht: 0, tva_pct: 0, mode: MODES[0], statut: "encaisse", justificatif_path: "", justificatif_name: "" }) || setRecDialog(true);
  const openDep = (d) => setDepForm(d || { date: new Date().toISOString().slice(0, 10), categorie: CATEGORIES[0], categorie_autre: "", description: "", fournisseur: "", amount_ht: 0, tva_pct: 0, mode: MODES[0], statut: "paye", justificatif_path: "", justificatif_name: "" }) || setDepDialog(true);

  const saveRec = async () => {
    setBusy(true);
    try {
      if (recForm.id) await api.patch(`/admin/finance/recettes/${recForm.id}`, recForm);
      else await api.post("/admin/finance/recettes", recForm);
      toast.success("Recette enregistrée"); setRecDialog(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const saveDep = async () => {
    setBusy(true);
    try {
      if (depForm.id) await api.patch(`/admin/finance/depenses/${depForm.id}`, depForm);
      else await api.post("/admin/finance/depenses", depForm);
      toast.success("Dépense enregistrée"); setDepDialog(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const delRec = async (id) => { try { await api.delete(`/admin/finance/recettes/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const delDep = async (id) => { try { await api.delete(`/admin/finance/depenses/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const saveTres = async () => {
    try { await api.put("/admin/finance/treasury", tres); toast.success("Trésorerie mise à jour"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendObjectifReport = async () => {
    try {
      const { data } = await api.post("/admin/finance/objectif-report");
      toast.success(data.emails_sent > 0 ? `Point objectif envoyé (${data.pct} %).` : (data.message || "Aucun objectif défini."));
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportReport = async (format) => {
    try {
      const q = `year=${reportY}${reportM ? `&month=${reportM}` : reportQ ? `&quarter=${reportQ}` : ""}&format=${format}`;
      const res = await api.get(`/admin/finance/report?${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `bilan-financier-${reportY}${reportM ? "-" + reportM : ""}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
  };

  const exportAnnuel = async () => {
    try {
      const res = await api.get(`/admin/finance/export-annuel?year=${reportY}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = `export-comptable-${reportY}.xlsx`; a.click();
      toast.success("Export comptable généré");
    } catch { toast.error("Export impossible"); }
  };

  const exportDashboardPdf = async () => {
    try {
      const res = await api.get(`/admin/finance/dashboard/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = `tableau-bord-finance.pdf`; a.click();
      toast.success("Rapport PDF généré et archivé dans « Rapports »");
    } catch { toast.error("Génération impossible"); }
  };

  const STATUT_STYLE = {
    encaisse: "bg-emerald-50 text-emerald-700 border-emerald-200", paye: "bg-emerald-50 text-emerald-700 border-emerald-200",
    attente: "bg-amber-50 text-amber-700 border-amber-200", annule: "bg-red-50 text-red-700 border-red-200",
  };
  const STATUT_LABEL = { encaisse: "Encaissé", paye: "Payé", attente: "En attente", annule: "Annulé" };

  if (!dash) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" size={26} /></div>;

  return (
    <div data-testid="finance-manager">
      <Tabs defaultValue="dashboard">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
          <TabsTrigger value="dashboard" data-testid="fin-tab-dashboard">Tableau de bord</TabsTrigger>
          <TabsTrigger value="recettes" data-testid="fin-tab-recettes">Recettes</TabsTrigger>
          <TabsTrigger value="depenses" data-testid="fin-tab-depenses">Dépenses</TabsTrigger>
          <TabsTrigger value="tresorerie" data-testid="fin-tab-tresorerie">Trésorerie</TabsTrigger>
          <TabsTrigger value="rapports" data-testid="fin-tab-rapports">Bilan & Rapports</TabsTrigger>
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="mt-6 space-y-6">
          <div className="flex justify-end">
            <Button data-testid="fin-dashboard-pdf" onClick={exportDashboardPdf} variant="outline" size="sm" className="rounded-full"><FileDown size={14} className="mr-1.5" /> Rapport PDF (→ Drive Rapports)</Button>
          </div>
          {dash.alertes.length > 0 && (
            <div className="space-y-2" data-testid="fin-alertes">
              {dash.alertes.map((a, i) => (
                a.type === "objectif" ? (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-800">
                    <CheckCircle size={16} className="shrink-0" /> {a.message}
                  </div>
                ) : (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                    <AlertTriangle size={16} className="shrink-0" /> {a.message}
                  </div>
                )
              ))}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KPI label="CA du jour" value={fmtXof(dash.ca_jour)} sub={conv(dash.ca_jour, fx)} testid="fin-kpi-jour" />
            <KPI label="CA du mois" value={fmtXof(dash.ca_mois)} sub={conv(dash.ca_mois, fx)} testid="fin-kpi-mois" />
            <KPI label="CA de l'année" value={fmtXof(dash.ca_annee)} sub={conv(dash.ca_annee, fx)} testid="fin-kpi-annee" />
            <KPI label="Recettes encaissées" value={fmtXof(dash.total_recettes)} sub={conv(dash.total_recettes, fx)} accent="text-emerald-600" testid="fin-kpi-recettes" />
            <KPI label="Dépenses engagées" value={fmtXof(dash.total_depenses)} sub={conv(dash.total_depenses, fx)} accent="text-red-600" testid="fin-kpi-depenses" />
            <KPI label="Bénéfice net" value={fmtXof(dash.benefice)} sub={`Marge : ${dash.marge} %`} accent={dash.benefice >= 0 ? "text-emerald-600" : "text-red-600"} testid="fin-kpi-benefice" />
          </div>

          {dash.objectif_ca_mensuel > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="fin-objectif-progress">
              <div className="flex items-center justify-between text-sm">
                <span className="font-display font-semibold text-navy">Objectif de CA — {new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
                <span className="font-mono text-slate-600">{fmtXof(dash.ca_mois)} / {fmtXof(dash.objectif_ca_mensuel)}</span>
              </div>
              <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full transition-all ${dash.objectif_progress >= 100 ? "bg-emerald-500" : "bg-ciel"}`} style={{ width: `${Math.min(dash.objectif_progress, 100)}%` }} />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <p className="text-xs text-slate-500">{dash.objectif_progress}% de l'objectif atteint</p>
                <Button data-testid="fin-objectif-report" onClick={sendObjectifReport} variant="outline" size="sm" className="rounded-full text-xs"><Send size={12} className="mr-1.5" /> Envoyer le point mensuel</Button>
              </div>
            </div>
          )}

          {dash.objectif_ca_annuel > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="fin-objectif-annuel-progress">
              <div className="flex items-center justify-between text-sm">
                <span className="font-display font-semibold text-navy">Objectif de CA annuel {new Date().getFullYear()}</span>
                <span className="font-mono text-slate-600">{fmtXof(dash.ca_annee)} / {fmtXof(dash.objectif_ca_annuel)}</span>
              </div>
              <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full transition-all ${dash.objectif_annuel_progress >= 100 ? "bg-emerald-500" : "bg-navy"}`} style={{ width: `${Math.min(dash.objectif_annuel_progress, 100)}%` }} />
              </div>
              <p className="mt-1.5 text-xs text-slate-500">{dash.objectif_annuel_progress}% de l'objectif annuel atteint</p>
            </div>
          )}

          <TrendAnalytics base="/admin/finance" testid="fin-analysis" />

          <RankingCard endpoint="/admin/finance/products-ranking" itemsKey="products" title="Classement produits & tendance" testid="fin-products-ranking" nameLabel="Produit" countLabel="Ventes" />

          <RankingCard endpoint="/admin/finance/clients-ranking" itemsKey="clients" title="Top clients & tendance" testid="fin-clients-ranking" nameLabel="Client" countLabel="Commandes" />

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="fin-chart-categories">
              <h3 className="font-display text-base font-semibold text-ciel">Répartition des dépenses</h3>
              {dash.depenses_cat.length === 0 ? (
                <p className="mt-8 text-center text-sm text-slate-400">Aucune dépense enregistrée.</p>
              ) : (
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={dash.depenses_cat} dataKey="montant" nameKey="categorie" cx="50%" cy="50%" outerRadius={80} label={(e) => `${Math.round(e.percent * 100)}%`}>
                        {dash.depenses_cat.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtXof(v)} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* RECETTES */}
        <TabsContent value="recettes" className="mt-6 space-y-8">
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-base font-semibold text-ciel">Recettes manuelles (hors boutique)</h3>
              <Button data-testid="fin-add-recette" onClick={() => openRec(null)} className="rounded-full bg-ciel text-white hover:bg-sky-700"><Plus size={15} className="mr-1.5" /> Ajouter</Button>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="fin-recettes-manuel">
              <table className="w-full min-w-[820px] text-sm">
                <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="p-3">Date</th><th className="p-3">Nature</th><th className="p-3">Description</th><th className="p-3">HT</th><th className="p-3">TVA</th><th className="p-3">TTC</th><th className="p-3">Mode</th><th className="p-3">Statut</th><th className="p-3"></th>
                </tr></thead>
                <tbody>
                  {recettes.manuel.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-400">Aucune recette manuelle.</td></tr>}
                  {recettes.manuel.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`fin-recette-${r.id}`}>
                      <td className="p-3 text-xs text-slate-500">{fmtDate(r.date)}</td>
                      <td className="p-3">{r.nature === "Autre" ? r.nature_autre || "Autre" : r.nature}</td>
                      <td className="max-w-[180px] truncate p-3 text-xs text-slate-500">{r.description}</td>
                      <td className="p-3 font-mono text-xs">{fmtXof(r.amount_ht)}</td>
                      <td className="p-3 text-xs">{r.tva_pct}%</td>
                      <td className="p-3 font-mono font-semibold">{fmtXof(r.amount_ttc)}</td>
                      <td className="p-3 text-xs">{r.mode}</td>
                      <td className="p-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUT_STYLE[r.statut]}`}>{STATUT_LABEL[r.statut]}</span></td>
                      <td className="whitespace-nowrap p-3">
                        <button onClick={() => { setRecForm(r); setRecDialog(true); }} className="mr-2 text-slate-400 hover:text-ciel" data-testid={`fin-edit-recette-${r.id}`}><Pencil size={14} /></button>
                        <button onClick={() => delRec(r.id)} className="text-slate-400 hover:text-red-500" data-testid={`fin-del-recette-${r.id}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="mb-3 font-display text-base font-semibold text-ciel">Recettes automatiques (ventes boutique)</h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="fin-recettes-auto">
              <table className="w-full min-w-[820px] text-sm">
                <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="p-3">Date</th><th className="p-3">Commande</th><th className="p-3">Client</th><th className="p-3">Produit</th><th className="p-3">TTC</th><th className="p-3">Mode</th>
                </tr></thead>
                <tbody>
                  {recettes.auto.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">Aucune vente enregistrée.</td></tr>}
                  {recettes.auto.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`fin-recette-auto-${r.id}`}>
                      <td className="p-3 text-xs text-slate-500">{fmtDate(r.date)}</td>
                      <td className="p-3 font-mono text-xs font-semibold text-navy">{r.order_id}</td>
                      <td className="p-3 text-xs text-slate-600">{r.client}</td>
                      <td className="max-w-[220px] truncate p-3 text-xs text-slate-500">{r.produit}</td>
                      <td className="p-3 font-mono font-semibold">{fmtXof(r.amount_ttc)}</td>
                      <td className="p-3 text-xs">{r.mode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* DEPENSES */}
        <TabsContent value="depenses" className="mt-6">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-base font-semibold text-ciel">Dépenses engagées</h3>
            <Button data-testid="fin-add-depense" onClick={() => openDep(null)} className="rounded-full bg-ciel text-white hover:bg-sky-700"><Plus size={15} className="mr-1.5" /> Ajouter</Button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="fin-depenses-table">
            <table className="w-full min-w-[880px] text-sm">
              <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
                <th className="p-3">Date</th><th className="p-3">Catégorie</th><th className="p-3">Bénéficiaire</th><th className="p-3">HT</th><th className="p-3">TVA</th><th className="p-3">TTC</th><th className="p-3">Mode</th><th className="p-3">Statut</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {depenses.depenses.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-slate-400">Aucune dépense.</td></tr>}
                {depenses.depenses.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100" data-testid={`fin-depense-${d.id}`}>
                    <td className="p-3 text-xs text-slate-500">{fmtDate(d.date)}</td>
                    <td className="max-w-[200px] truncate p-3 text-xs">{d.categorie === "Autre" ? d.categorie_autre || "Autre" : d.categorie}</td>
                    <td className="p-3 text-xs text-slate-600">{d.fournisseur}</td>
                    <td className="p-3 font-mono text-xs">{fmtXof(d.amount_ht)}</td>
                    <td className="p-3 text-xs">{d.tva_pct}%</td>
                    <td className="p-3 font-mono font-semibold">{fmtXof(d.amount_ttc)}</td>
                    <td className="p-3 text-xs">{d.mode}</td>
                    <td className="p-3"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUT_STYLE[d.statut]}`}>{STATUT_LABEL[d.statut]}</span></td>
                    <td className="whitespace-nowrap p-3">
                      <button onClick={() => { setDepForm(d); setDepDialog(true); }} className="mr-2 text-slate-400 hover:text-ciel" data-testid={`fin-edit-depense-${d.id}`}><Pencil size={14} /></button>
                      <button onClick={() => delDep(d.id)} className="text-slate-400 hover:text-red-500" data-testid={`fin-del-depense-${d.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* TRESORERIE */}
        <TabsContent value="tresorerie" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="fin-tresorerie-suivi">
              <div className="flex items-center gap-2"><Wallet size={18} className="text-ciel" /><h3 className="font-display text-base font-semibold text-ciel">Suivi de trésorerie</h3></div>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Solde initial</span><span className="font-mono">{fmtXof(dash.tresorerie.solde_initial)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">+ Entrées (recettes)</span><span className="font-mono text-emerald-600">{fmtXof(dash.tresorerie.entrees)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">− Sorties (dépenses)</span><span className="font-mono text-red-600">{fmtXof(dash.tresorerie.sorties)}</span></div>
                <div className="border-t border-slate-200 pt-3 flex justify-between">
                  <span className="font-semibold text-navy">Solde actuel</span>
                  <span className={`font-mono text-lg font-bold ${dash.tresorerie.solde_actuel >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="fin-solde-actuel">{fmtXof(dash.tresorerie.solde_actuel)}</span>
                </div>
                <p className="text-xs text-slate-400">{conv(dash.tresorerie.solde_actuel, fx)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="fin-tresorerie-config">
              <h3 className="font-display text-base font-semibold text-ciel">Paramètres</h3>
              <div className="mt-5 space-y-4">
                <div>
                  <Label className="text-xs font-medium text-slate-500">Solde initial (FCFA)</Label>
                  <Input type="number" data-testid="fin-solde-initial" value={tres.solde_initial} className="mt-1.5 font-mono" onChange={(e) => setTres({ ...tres, solde_initial: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Seuil d'alerte (FCFA)</Label>
                  <Input type="number" data-testid="fin-seuil-alerte" value={tres.seuil_alerte} className="mt-1.5 font-mono" onChange={(e) => setTres({ ...tres, seuil_alerte: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Objectif de CA mensuel (FCFA)</Label>
                  <Input type="number" data-testid="fin-objectif-ca" value={tres.objectif_ca_mensuel} className="mt-1.5 font-mono" onChange={(e) => setTres({ ...tres, objectif_ca_mensuel: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-500">Objectif de CA annuel (FCFA)</Label>
                  <Input type="number" data-testid="fin-objectif-ca-annuel" value={tres.objectif_ca_annuel} className="mt-1.5 font-mono" onChange={(e) => setTres({ ...tres, objectif_ca_annuel: parseFloat(e.target.value) || 0 })} />
                </div>
                <Button data-testid="fin-save-tresorerie" onClick={saveTres} className="w-full rounded-full bg-navy text-white hover:bg-navy-light">Enregistrer</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* RAPPORTS */}
        <TabsContent value="rapports" className="mt-6">
          <div className="max-w-xl rounded-xl border border-slate-200 bg-white p-6" data-testid="fin-rapports-panel">
            <h3 className="font-display text-base font-semibold text-ciel">Bilan financier — Cabinet CARIS Santé</h3>
            <p className="mt-1 text-xs text-slate-500">Rapport mensuel ou annuel (recettes vs dépenses, bénéfice net, variations).</p>
            <div className="mt-5 grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-medium text-slate-500">Année</Label>
                <Input type="number" data-testid="fin-report-year" value={reportY} className="mt-1.5 font-mono" onChange={(e) => setReportY(parseInt(e.target.value) || new Date().getFullYear())} />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">Mois</Label>
                <Select value={reportM || "all"} onValueChange={(v) => { setReportM(v === "all" ? "" : v); if (v !== "all") setReportQ(""); }}>
                  <SelectTrigger className="mt-1.5" data-testid="fin-report-month"><SelectValue placeholder="Toute l'année" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toute l'année</SelectItem>
                    {[...Array(12)].map((_, i) => <SelectItem key={i} value={String(i + 1)}>{new Date(2000, i, 1).toLocaleDateString("fr-FR", { month: "long" })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500">Trimestre</Label>
                <Select value={reportQ || "none"} onValueChange={(v) => { setReportQ(v === "none" ? "" : v); if (v !== "none") setReportM(""); }}>
                  <SelectTrigger className="mt-1.5" data-testid="fin-report-quarter"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    <SelectItem value="1">T1 (Jan–Mar)</SelectItem>
                    <SelectItem value="2">T2 (Avr–Juin)</SelectItem>
                    <SelectItem value="3">T3 (Juil–Sep)</SelectItem>
                    <SelectItem value="4">T4 (Oct–Déc)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <Button data-testid="fin-export-pdf" onClick={() => exportReport("pdf")} className="rounded-full bg-navy text-white hover:bg-navy-light"><FileDown size={14} className="mr-1.5" /> PDF</Button>
              <Button data-testid="fin-export-xlsx" onClick={() => exportReport("xlsx")} variant="outline" className="rounded-full"><FileDown size={14} className="mr-1.5" /> Excel</Button>
            </div>
            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="font-display text-sm font-semibold text-navy">Export comptable annuel {reportY}</p>
              <p className="mt-1 text-xs text-slate-500">Classeur Excel multi-feuilles (Synthèse mensuelle, Recettes, Dépenses, Paie) prêt pour votre comptable.</p>
              <Button data-testid="fin-export-annuel" onClick={exportAnnuel} className="mt-3 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"><FileDown size={14} className="mr-1.5" /> Télécharger l'export comptable</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Recette */}
      <Dialog open={recDialog} onOpenChange={setRecDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="fin-recette-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{recForm?.id ? "Modifier" : "Nouvelle"} recette</DialogTitle></DialogHeader>
          {recForm && (
            <div className="mt-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" data-testid="fin-rec-date" value={recForm.date?.slice(0, 10)} className="mt-1.5" onChange={(e) => setRecForm({ ...recForm, date: e.target.value })} /></div>
                <div>
                  <Label className="text-xs text-slate-500">Nature</Label>
                  <Select value={recForm.nature} onValueChange={(v) => setRecForm({ ...recForm, nature: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="fin-rec-nature"><SelectValue /></SelectTrigger>
                    <SelectContent>{NATURES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {recForm.nature === "Autre" && <Input placeholder="Préciser la nature" data-testid="fin-rec-nature-autre" value={recForm.nature_autre} onChange={(e) => setRecForm({ ...recForm, nature_autre: e.target.value })} />}
              <div><Label className="text-xs text-slate-500">Description</Label><Textarea data-testid="fin-rec-desc" value={recForm.description} className="mt-1.5" onChange={(e) => setRecForm({ ...recForm, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-500">Montant HT</Label><Input type="number" data-testid="fin-rec-ht" value={recForm.amount_ht} className="mt-1.5 font-mono" onChange={(e) => setRecForm({ ...recForm, amount_ht: parseFloat(e.target.value) || 0 })} /></div>
                <div>
                  <Label className="text-xs text-slate-500">TVA</Label>
                  <Select value={String(recForm.tva_pct)} onValueChange={(v) => setRecForm({ ...recForm, tva_pct: parseInt(v) })}>
                    <SelectTrigger className="mt-1.5" data-testid="fin-rec-tva"><SelectValue /></SelectTrigger>
                    <SelectContent>{TVA_OPTIONS.map((t) => <SelectItem key={t} value={String(t)}>{t}%</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-slate-500">TTC</Label><Input readOnly value={fmtXof(recForm.amount_ht * (1 + recForm.tva_pct / 100))} className="mt-1.5 bg-slate-50 font-mono text-xs" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Mode de règlement</Label>
                  <Select value={recForm.mode} onValueChange={(v) => setRecForm({ ...recForm, mode: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="fin-rec-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Statut</Label>
                  <Select value={recForm.statut} onValueChange={(v) => setRecForm({ ...recForm, statut: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="fin-rec-statut"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="encaisse">Encaissé</SelectItem><SelectItem value="attente">En attente</SelectItem><SelectItem value="annule">Annulé</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <Justificatif path={recForm.justificatif_path} name={recForm.justificatif_name} testid="fin-rec-justif" onChange={(p, n) => setRecForm({ ...recForm, justificatif_path: p, justificatif_name: n })} />
              <Button data-testid="fin-rec-save" onClick={saveRec} disabled={busy} className="w-full rounded-full bg-ciel py-5 text-white hover:bg-sky-700">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Dépense */}
      <Dialog open={depDialog} onOpenChange={setDepDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="fin-depense-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">{depForm?.id ? "Modifier" : "Nouvelle"} dépense</DialogTitle></DialogHeader>
          {depForm && (
            <div className="mt-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-slate-500">Date</Label><Input type="date" data-testid="fin-dep-date" value={depForm.date?.slice(0, 10)} className="mt-1.5" onChange={(e) => setDepForm({ ...depForm, date: e.target.value })} /></div>
                <div>
                  <Label className="text-xs text-slate-500">Catégorie</Label>
                  <Select value={depForm.categorie} onValueChange={(v) => setDepForm({ ...depForm, categorie: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="fin-dep-cat"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {depForm.categorie === "Autre" && <Input placeholder="Préciser la catégorie" data-testid="fin-dep-cat-autre" value={depForm.categorie_autre} onChange={(e) => setDepForm({ ...depForm, categorie_autre: e.target.value })} />}
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs text-slate-500">Description</Label><Input data-testid="fin-dep-desc" value={depForm.description} className="mt-1.5" onChange={(e) => setDepForm({ ...depForm, description: e.target.value })} /></div>
                <div><Label className="text-xs text-slate-500">Fournisseur / Bénéficiaire</Label><Input data-testid="fin-dep-fournisseur" value={depForm.fournisseur} className="mt-1.5" onChange={(e) => setDepForm({ ...depForm, fournisseur: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs text-slate-500">Montant HT</Label><Input type="number" data-testid="fin-dep-ht" value={depForm.amount_ht} className="mt-1.5 font-mono" onChange={(e) => setDepForm({ ...depForm, amount_ht: parseFloat(e.target.value) || 0 })} /></div>
                <div>
                  <Label className="text-xs text-slate-500">TVA</Label>
                  <Select value={String(depForm.tva_pct)} onValueChange={(v) => setDepForm({ ...depForm, tva_pct: parseInt(v) })}>
                    <SelectTrigger className="mt-1.5" data-testid="fin-dep-tva"><SelectValue /></SelectTrigger>
                    <SelectContent>{TVA_OPTIONS.map((t) => <SelectItem key={t} value={String(t)}>{t}%</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-slate-500">TTC</Label><Input readOnly value={fmtXof(depForm.amount_ht * (1 + depForm.tva_pct / 100))} className="mt-1.5 bg-slate-50 font-mono text-xs" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Mode de règlement</Label>
                  <Select value={depForm.mode} onValueChange={(v) => setDepForm({ ...depForm, mode: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="fin-dep-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Statut</Label>
                  <Select value={depForm.statut} onValueChange={(v) => setDepForm({ ...depForm, statut: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="fin-dep-statut"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="paye">Payé</SelectItem><SelectItem value="attente">En attente</SelectItem><SelectItem value="annule">Annulé</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <Justificatif path={depForm.justificatif_path} name={depForm.justificatif_name} testid="fin-dep-justif" onChange={(p, n) => setDepForm({ ...depForm, justificatif_path: p, justificatif_name: n })} />
              <Button data-testid="fin-dep-save" onClick={saveDep} disabled={busy} className="w-full rounded-full bg-ciel py-5 text-white hover:bg-sky-700">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
